import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { action } from '@ember/object'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { eq } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import formatValue from 'teacher-ui-client/helpers/format-value'
import type { MyStudentsSpellingData } from 'teacher-ui-client/components/writing-legends/my-students-spelling'
import { LinkTo } from '@ember/routing'
import { hash } from '@ember/helper'

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    studentsReportData?: MyStudentsSpellingData
  }
}

export const StudentSpellingTable: TOC<Signature> = <template>
  <table class="divide-dusty-black-200 relative table-fixed divide-y" ...attributes>
    <thead>
      <tr class="bg-white">
        <ThSort @sortKey="first_name" @text={{t "reporting.firstName"}} />
        <ThSort @sortKey="last_name" @text={{t "reporting.lastName"}} />
        <ThSort
          @sortKey="total_correct"
          @text={{t "components.reporting.tables.writingLegends.spelling.totalCorrect"}}
        />
        <ThSort
          @sortKey="total_incorrect"
          @text={{t "components.reporting.tables.writingLegends.spelling.totalIncorrect"}}
        />
        <ThSort @sortKey="error_rate" @text={{t "components.reporting.tables.writingLegends.spelling.errorRate"}} />
      </tr>
    </thead>
    <tbody class="divide-dusty-black-200 divide-y {{if @isReloading 'opacity-50'}}">
      {{#each @studentsReportData.students as |student index|}}
        <tr class="bg-white" data-test-table-row="{{index}}">
          <td class="truncate p-3" data-test-cell="first_name">
            {{#if (eq student.error_rate null)}}
              {{student.first_name}}
            {{else}}
              <LinkTo @query={{hash student=student.id sort="-last_attempted_at"}}>{{student.first_name}}</LinkTo>
            {{/if}}
          </td>
          <td class="truncate p-3" data-test-cell="last_name">
            {{#if (eq student.error_rate null)}}
              {{student.last_name}}
            {{else}}
              <LinkTo @query={{hash student=student.id sort="-last_attempted_at"}}>{{student.last_name}}</LinkTo>
            {{/if}}
          </td>
          <td class="p-3" data-test-cell="total_correct">
            {{student.total_correct}}
          </td>
          <td class="p-3" data-test-cell="total_incorrect">
            {{student.total_incorrect}}
          </td>
          <td class="p-3" data-test-cell="error_rate">
            {{#if (eq student.error_rate null)}}
              <span>-</span>
            {{else}}
              {{formatValue student.error_rate append="%"}}
            {{/if}}
          </td>
        </tr>
      {{/each}}
    </tbody>
  </table>
</template>

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/writing-legends/spelling') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  updateSort() {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="h-[80px] select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
